import { useQuery } from "@tanstack/react-query"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import type { CuentaCliente, CuentaEmpleado, PartidaCuenta } from "@/componentes/ingresos/tipos"
import { aCentimos, comoMonto, monto } from "@/componentes/tesoreria/dinero"
import { Cajon } from "@/componentes/ui/Cajon"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

const TIPO: Record<string, string> = {
  CXC: "Cuenta por cobrar",
  INGRESO: "Ingreso",
  COBRO: "Cobro",
  APLICACION: "Ingreso aplicado",
  ANTICIPO: "Anticipo pagado",
  PRESTAMO: "Préstamo pagado",
}

/**
 * La cuenta de un cliente o de un empleado: su saldo por moneda y las partidas que lo
 * explican, con el saldo después de cada una. Nada se teclea: sale de sus CxC, sus
 * ingresos y sus anticipos, así que anular o corregir uno corrige la cuenta.
 *
 * ⚠ **Por moneda, nunca sumado**: un anticipo en bolívares no paga una CxC en dólares.
 */
export function EstadoCuentaTercero({
  tipo,
  id,
  nombre,
  onCerrar,
}: {
  tipo: "CLIENTE" | "EMPLEADO"
  id: number
  nombre: string
  onCerrar: () => void
}) {
  return (
    <Cajon abierto titulo={`Estado de cuenta · ${nombre}`} onCerrar={onCerrar} ancho="amplio">
      <DetalleEstadoCuenta tipo={tipo} id={id} nombre={nombre} />
    </Cajon>
  )
}

/**
 * El estado de cuenta sin el cajón: lo pinta Tesorería dentro de su cajón y la ficha del
 * empleado en su expediente laboral (03/11). Una sola pieza, para que digan lo mismo.
 */
export function DetalleEstadoCuenta({
  tipo,
  id,
  nombre,
}: {
  tipo: "CLIENTE" | "EMPLEADO"
  id: number
  nombre: string
}) {
  const esCliente = tipo === "CLIENTE"
  const cuenta = useQuery<CuentaCliente | CuentaEmpleado>({
    queryKey: [esCliente ? "cuenta-cliente" : "cuenta-empleado", id],
    queryFn: () =>
      esCliente
        ? api.get<CuentaCliente>(`/api/v1/cuentas-clientes/${id}`)
        : api.get<CuentaEmpleado>(`/api/v1/cuentas-empleados/${id}`),
  })
  const partidas: PartidaCuenta[] = cuenta.data?.partidas ?? []

  return (
    <>
      {cuenta.isPending && <EstadoCarga etiqueta="Cargando el estado de cuenta…" />}
      {cuenta.isError && (
        <EstadoError
          titulo="No se pudo cargar el estado de cuenta."
          error={cuenta.error}
          onReintentar={() => cuenta.refetch()}
        />
      )}
      {cuenta.data && (
        <div className="space-y-5 text-sm">
          <div className="flex flex-wrap justify-end gap-2">
            <Exportar
              titulo={`Estado de cuenta · ${nombre}`}
              subtitulo={`${esCliente ? "Cliente" : "Empleado"} ${nombre}: saldo por moneda y partidas`}
              documento={() => documentoDeCuenta(nombre, esCliente, cuenta.data, partidas)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {cuenta.data.saldos.length === 0 && (
              <p style={{ color: "var(--fg-muted)" }}>Sin saldo en ninguna moneda.</p>
            )}
            {"cliente_id" in cuenta.data
              ? cuenta.data.saldos.map((s) => {
                  const neto = aCentimos(s.neto)
                  return (
                    <div key={s.divisa} className="tarjeta space-y-1 p-3">
                      <p
                        className="text-xs font-semibold uppercase"
                        style={{ color: "var(--fg-muted)" }}
                      >
                        {s.divisa}
                      </p>
                      <p className="text-lg font-semibold tabular-nums">
                        {comoMonto(neto < 0n ? -neto : neto)} {s.divisa}
                      </p>
                      <p style={{ color: "var(--fg-muted)" }}>
                        {neto > 0n ? "lo que debe" : neto < 0n ? "a su favor" : "sin saldo"}
                      </p>
                      <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                        Pendiente {monto(s.pendiente)} · anticipo {monto(s.anticipo)}
                      </p>
                    </div>
                  )
                })
              : cuenta.data.saldos.map((s) => {
                  const debe = aCentimos(s.debe)
                  return (
                    <div key={s.divisa} className="tarjeta space-y-1 p-3">
                      <p
                        className="text-xs font-semibold uppercase"
                        style={{ color: "var(--fg-muted)" }}
                      >
                        {s.divisa}
                      </p>
                      <p className="text-lg font-semibold tabular-nums">
                        {comoMonto(debe < 0n ? -debe : debe)} {s.divisa}
                      </p>
                      <p style={{ color: "var(--fg-muted)" }}>
                        {debe > 0n
                          ? "lo que debe por anticipos y préstamos"
                          : debe < 0n
                            ? "devolvió de más: la agencia le debe"
                            : "sin saldo"}
                      </p>
                    </div>
                  )
                })}
          </div>

          <Tabla etiqueta="Partidas del estado de cuenta">
            <Cabecera>
              <Th>Fecha</Th>
              <Th>Referencia</Th>
              <Th>Concepto</Th>
              <Th derecha>Movimiento</Th>
              <Th derecha>Saldo</Th>
            </Cabecera>
            <Cuerpo>
              {partidas.length === 0 && (
                <TablaVacia colSpan={5}>Todavía no hay movimientos en su cuenta.</TablaVacia>
              )}
              {partidas.map((p) => (
                <Fila
                  key={`${p.tipo}-${p.referencia}-${p.fecha}-${p.divisa}-${p.efecto}-${p.saldo}`}
                >
                  <Td numero>{comoFecha(p.fecha)}</Td>
                  <Td numero>{p.referencia}</Td>
                  <Td>
                    <span className="block">{TIPO[p.tipo] ?? p.tipo}</span>
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      {p.descripcion}
                    </span>
                  </Td>
                  <Td derecha numero>
                    {p.tipo === "APLICACION" ? "—" : `${monto(p.efecto)} ${p.divisa}`}
                  </Td>
                  <Td derecha numero>
                    {monto(p.saldo)} {p.divisa}
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Saldo positivo: {esCliente ? "el cliente" : "el empleado"} le debe a la agencia.
            Negativo: tiene saldo a su favor.
          </p>
        </div>
      )}
    </>
  )
}

/** La cuenta con la moneda en su columna: un saldo sin moneda no dice nada. */
function documentoDeCuenta(
  nombre: string,
  esCliente: boolean,
  cuenta: CuentaCliente | CuentaEmpleado,
  partidas: PartidaCuenta[],
): DocumentoExportable {
  const saldos =
    "cliente_id" in cuenta
      ? {
          titulo: "Saldo por moneda",
          columnas: [
            { titulo: "Moneda", tipo: "texto" as const },
            { titulo: "Pendiente", tipo: "monto" as const },
            { titulo: "Anticipo", tipo: "monto" as const },
            { titulo: "Neto", tipo: "monto" as const },
          ],
          filas: cuenta.saldos.map((s) => ({
            celdas: [s.divisa, Number(s.pendiente), Number(s.anticipo), Number(s.neto)],
          })),
        }
      : {
          titulo: "Saldo por moneda",
          columnas: [
            { titulo: "Moneda", tipo: "texto" as const },
            { titulo: "Debe", tipo: "monto" as const },
          ],
          filas: cuenta.saldos.map((s) => ({ celdas: [s.divisa, Number(s.debe)] })),
        }
  return {
    titulo: `Estado de cuenta · ${nombre}`,
    subtitulo: `${esCliente ? "Cliente" : "Empleado"} ${nombre}: saldo por moneda y partidas`,
    secciones: [
      saldos,
      {
        titulo: "Partidas del estado de cuenta",
        columnas: [
          { titulo: "Fecha", tipo: "fecha" },
          { titulo: "Referencia", tipo: "texto" },
          { titulo: "Tipo", tipo: "texto" },
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Movimiento", tipo: "monto" },
          { titulo: "Saldo", tipo: "monto" },
        ],
        filas: partidas.map((p) => ({
          celdas: [
            p.fecha.slice(0, 10),
            p.referencia,
            TIPO[p.tipo] ?? p.tipo,
            p.descripcion,
            p.divisa,
            p.tipo === "APLICACION" ? null : Number(p.efecto),
            Number(p.saldo),
          ],
        })),
      },
    ],
  }
}
